import { useState } from 'react';
import { Target, ChevronDown, AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAgeRecommendations } from '@/hooks/useAgeRecommendations';

interface Props {
  totalProteinG: number;
  totalCaloriesKcal: number;
  targetCaloriesKcal: number;
}

export function AgeFocusCard({ totalProteinG, totalCaloriesKcal, targetCaloriesKcal }: Props) {
  const { isSeniorFocus, ageGroup, getDayFocus } = useAgeRecommendations();
  const [expanded, setExpanded] = useState(false);

  if (!isSeniorFocus) return null;

  const focus = getDayFocus(totalProteinG, totalCaloriesKcal, targetCaloriesKcal);
  if (!focus) return null;

  const proteinColor =
    focus.protein.status === 'great' ? 'text-emerald-glow' :
    focus.protein.status === 'good' ? 'text-sky-glow' : 'text-warn';

  const proteinIcon =
    focus.protein.status === 'great' ? <CheckCircle2 size={14} className="text-emerald-glow" /> :
    focus.protein.status === 'good' ? <Info size={14} className="text-sky-glow" /> :
    <AlertCircle size={14} className="text-warn" />;

  return (
    <div className="card-elevated p-4 mb-5 border-emerald-glow/15">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 text-left"
      >
        <div className="w-9 h-9 rounded-xl bg-emerald-glow/10 text-emerald-glow flex items-center justify-center shrink-0">
          <Target size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">
            Возрастной фокус ({ageGroup})
          </div>
          <div className="text-[11px] text-ink-dim truncate">
            {focus.protein.status === 'great' ? '✅ ' : focus.protein.status === 'low' ? '⚠️ ' : '• '}
            Белок: {focus.protein.value}/{focus.protein.target}г
          </div>
        </div>
        <ChevronDown
          size={14}
          className={`text-ink-dim shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 mt-3 border-t border-line/40 space-y-3">
              {/* Белок */}
              <div className="flex gap-2">
                {proteinIcon}
                <div className="flex-1">
                  <div className="text-xs font-medium">
                    Белок · <span className={proteinColor}>{focus.protein.value}г</span>
                    <span className="text-ink-dim"> / {focus.protein.target}г</span>
                  </div>
                  <p className="text-[11px] text-ink-muted leading-relaxed mt-0.5">
                    {focus.protein.text}
                  </p>
                </div>
              </div>

              {/* Подсказки */}
              {focus.hints.map((hint, idx) => (
                <div key={idx} className="flex gap-2">
                  <Info size={14} className="text-ink-dim shrink-0 mt-0.5" />
                  <p className="text-[11px] text-ink-muted leading-relaxed">{hint}</p>
                </div>
              ))}

              {/* Disclaimer */}
              <div className="bg-bg-elevated rounded-lg p-2.5 flex gap-2">
                <Info size={12} className="text-ink-dim shrink-0 mt-0.5" />
                <p className="text-[9px] text-ink-dim leading-relaxed">
                  Подсказки общие и не заменяют консультацию врача. При заболеваниях обсудите рацион со специалистом.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
